import React from "react";
import {
  Paper,
  Box,
  Typography,
  Select,
  MenuItem,
  FormControl,
  RadioGroup,
  FormControlLabel,
  Radio,
  Stack,
  Divider,
} from "@mui/material";
import { LanguageSettings } from "shared/types";

interface LanguageSectionProps {
  settings: LanguageSettings;
  onChange: (key: keyof LanguageSettings, value: string) => void;
}

const radioSx = {
  color: "#178B8E",
  "&.Mui-checked": { color: "#178B8E" },
};

const SelectRow: React.FC<{
  label: string;
  description?: string;
  value: string;
  options: { value: string; label: string }[];
  onChange: (value: string) => void;
}> = ({ label, description, value, options, onChange }) => (
  <Box
    sx={{
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      py: 1.5,
      gap: 2,
    }}
  >
    <Box sx={{ flex: 1 }}>
      <Typography variant="body2" fontWeight={500}>
        {label}
      </Typography>
      {description && (
        <Typography variant="caption" color="text.secondary">
          {description}
        </Typography>
      )}
    </Box>
    <FormControl size="small" sx={{ minWidth: 200 }}>
      <Select value={value} onChange={(e) => onChange(e.target.value)}>
        {options.map((opt) => (
          <MenuItem key={opt.value} value={opt.value}>
            {opt.label}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  </Box>
);

const LanguageSection: React.FC<LanguageSectionProps> = ({
  settings,
  onChange,
}) => {
  return (
    <Paper sx={{ p: 3, borderRadius: 2, mb: 3 }}>
      <Typography variant="h6" fontWeight={600} mb={0.5}>
        Language & Region
      </Typography>
      <Typography variant="body2" color="text.secondary" mb={2}>
        Customize your language and regional preferences
      </Typography>

      <Stack divider={<Divider />}>
        <SelectRow
          label="Language"
          value={settings.language}
          options={[
            { value: "en-US", label: "English (US)" },
            { value: "en-GB", label: "English (UK)" },
            { value: "es", label: "Spanish" },
            { value: "fr", label: "French" },
            { value: "de", label: "German" },
            { value: "sr", label: "Serbian" },
          ]}
          onChange={(v) => onChange("language", v)}
        />
        <SelectRow
          label="Date Format"
          value={settings.dateFormat}
          options={[
            { value: "MM/DD/YYYY", label: "MM/DD/YYYY (US)" },
            { value: "DD/MM/YYYY", label: "DD/MM/YYYY (European)" },
            { value: "YYYY-MM-DD", label: "YYYY-MM-DD (ISO)" },
          ]}
          onChange={(v) => onChange("dateFormat", v)}
        />

        {/* Time Format — Radio */}
        <Box sx={{ py: 1.5 }}>
          <Typography variant="body2" fontWeight={500} mb={0.75}>
            Time Format
          </Typography>
          <RadioGroup
            row
            value={settings.timeFormat}
            onChange={(e) => onChange("timeFormat", e.target.value)}
          >
            <FormControlLabel
              value="12-hour"
              control={<Radio size="small" sx={radioSx} />}
              label={<Typography variant="body2">12-hour (3:00 PM)</Typography>}
            />
            <FormControlLabel
              value="24-hour"
              control={<Radio size="small" sx={radioSx} />}
              label={<Typography variant="body2">24-hour (15:00)</Typography>}
            />
          </RadioGroup>
        </Box>

        <SelectRow
          label="Currency"
          value={settings.currency}
          options={[
            { value: "USD", label: "USD ($)" },
            { value: "EUR", label: "EUR (€)" },
            { value: "GBP", label: "GBP (£)" },
            { value: "RSD", label: "RSD (din)" },
          ]}
          onChange={(v) => onChange("currency", v)}
        />
        <SelectRow
          label="Timezone"
          description="Used for booking times and reminders"
          value={settings.timezone}
          options={[
            { value: "auto", label: "Auto-detect" },
            { value: "America/New_York", label: "Eastern Time (ET)" },
            { value: "America/Chicago", label: "Central Time (CT)" },
            { value: "America/Denver", label: "Mountain Time (MT)" },
            { value: "America/Los_Angeles", label: "Pacific Time (PT)" },
            { value: "Europe/London", label: "London (GMT)" },
            { value: "Europe/Paris", label: "Paris (CET)" },
            { value: "Europe/Belgrade", label: "Belgrade (CET)" },
          ]}
          onChange={(v) => onChange("timezone", v)}
        />
      </Stack>
    </Paper>
  );
};

export default LanguageSection;
